import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, User, Building, Phone, Mail, Calendar, Edit, Trash2,
  Globe, Linkedin, Facebook, Instagram, MapPin, MessageCircle, Send,
  FileText, Clock, Check, X, Plus, ChevronRight, History, Target
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Métiers du bâtiment
const sectorOptions = [
  { value: 'maconnerie', label: 'Maçonnerie' },
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'electricite', label: 'Électricité' },
  { value: 'carrelage', label: 'Carrelage' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'menuiserie', label: 'Menuiserie' },
  { value: 'charpente_couverture', label: 'Charpente/Couverture' },
  { value: 'platrerie', label: 'Plâtrerie' },
  { value: 'chauffage_clim', label: 'Chauffage/Climatisation' },
  { value: 'terrassement', label: 'Terrassement' },
  { value: 'isolation', label: 'Isolation' },
  { value: 'pisciniste', label: 'Pisciniste' },
  { value: 'jardinier', label: 'Jardinier' },
  { value: 'paysagiste', label: 'Paysagiste' },
  { value: 'serrurerie', label: 'Serrurerie' },
  { value: 'facade', label: 'Façade' },
  { value: 'multi_corps', label: 'Multi-corps d\'état' },
  { value: 'autre', label: 'Autre' }
];

const statusLabels = {
  new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-600' },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-600' },
  quote_todo: { label: 'Devis à faire', color: 'bg-amber-100 text-amber-600' },
  quote_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-600' },
  waiting: { label: 'En attente', color: 'bg-pink-100 text-pink-600' },
  won: { label: 'Gagné', color: 'bg-green-100 text-green-600' },
  lost: { label: 'Perdu', color: 'bg-red-100 text-red-600' }
};

const pipelineStages = [
  { id: 'new', label: 'Nouveau' },
  { id: 'contacted', label: 'Contacté' },
  { id: 'meeting', label: 'RDV planifié' },
  { id: 'quote_sent', label: 'Devis envoyé' },
  { id: 'negotiation', label: 'Négociation' },
  { id: 'won', label: 'Gagné' },
  { id: 'lost', label: 'Perdu' }
];

const emailStatusLabels = {
  sent: { label: 'Envoyé', color: 'bg-blue-100 text-blue-600' },
  delivered: { label: 'Délivré', color: 'bg-green-100 text-green-600' },
  opened: { label: 'Ouvert', color: 'bg-purple-100 text-purple-600' },
  clicked: { label: 'Cliqué', color: 'bg-amber-100 text-amber-600' },
  bounced: { label: 'Échec', color: 'bg-red-100 text-red-600' }
};

export default function AdminProspectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', visible_to_prospect: false });
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    fetchProspect();
    fetchQuotes();
  }, [id]);

  const fetchProspect = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/prospects/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // API returns {prospect, quotes} - extract the prospect object
        setProspect(data.prospect || data);
        // Also set quotes from the response if available
        if (data.quotes) {
          setQuotes(data.quotes);
        }
      } else {
        toast.error('Prospect non trouvé');
        navigate('/admin/crm/prospects');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/quotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const allQuotes = await response.json();
        setQuotes(allQuotes.filter(q => q.prospect_id === id));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/prospects/${id}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchProspect();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePipelineChange = async (newStage) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/pipeline/move`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prospect_id: id, new_stage: newStage })
      });
      if (response.ok) {
        toast.success('Prospect déplacé dans le pipeline');
        fetchProspect();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/prospects/${id}/notes`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newNote)
      });
      if (response.ok) {
        toast.success('Note ajoutée');
        setShowNoteModal(false);
        setNewNote({ content: '', visible_to_prospect: false });
        fetchProspect();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openWhatsApp = () => {
    const phone = (prospect.whatsapp || prospect.phone || '').replace(/\s/g, '').replace(/^0/, '+33');
    const message = encodeURIComponent(`Bonjour ${prospect.name}, je te contacte de BatiAssist...`);
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  const getSectorLabel = (value) => {
    const sector = sectorOptions.find(s => s.value === value);
    return sector?.label || value || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!prospect) return null;

  const status = statusLabels[prospect.status] || statusLabels.new;

  return (
    <div className="space-y-6" data-testid="prospect-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/crm/prospects')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{prospect.name}</h1>
            <span className={`text-sm px-3 py-1 rounded-full ${status.color}`}>{status.label}</span>
          </div>
          <p className="text-gray-500">{prospect.company_name || 'Pas d\'entreprise'} • {getSectorLabel(prospect.sector)}</p>
        </div>
        <div className="flex gap-2">
          {(prospect.whatsapp || prospect.phone) && (
            <Button variant="outline" onClick={openWhatsApp} className="text-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          )}
          <Link to={`/admin/crm/quotes?action=new&prospect_id=${prospect.id}`}>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <FileText className="w-4 h-4 mr-2" />
              Créer un devis
            </Button>
          </Link>
        </div>
      </div>

      {/* Pipeline Stage Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Pipeline Commercial
        </h3>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {pipelineStages.map((stage, index) => (
            <button
              key={stage.id}
              onClick={() => handlePipelineChange(stage.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                prospect.pipeline_stage === stage.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {index + 1}
              </span>
              {stage.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'info', label: 'Informations', icon: User },
          { id: 'notes', label: 'Notes', icon: FileText, count: prospect.notes?.length },
          { id: 'emails', label: 'Emails', icon: Mail, count: prospect.emails?.length },
          { id: 'quotes', label: 'Devis', icon: FileText, count: quotes.length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === tab.id 
                ? 'text-blue-600 border-blue-600' 
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count > 0 && (
              <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Contact
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${prospect.email}`} className="text-blue-600 hover:underline">{prospect.email}</a>
              </div>
              {prospect.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${prospect.phone}`} className="hover:underline">{prospect.phone}</a>
                </div>
              )}
              {prospect.whatsapp && (
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  <span>{prospect.whatsapp}</span>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Adresse
            </h3>
            <div className="text-gray-600">
              {prospect.address ? (
                <>
                  <p>{prospect.address}</p>
                  <p>{prospect.postal_code} {prospect.city}</p>
                  <p>{prospect.country || 'France'}</p>
                </>
              ) : (
                <p className="text-gray-400">Aucune adresse renseignée</p>
              )}
            </div>
          </div>

          {/* Company */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Entreprise
            </h3>
            <div className="space-y-2 text-gray-600">
              <p><span className="text-gray-400">Nom :</span> {prospect.company_name || '-'}</p>
              <p><span className="text-gray-400">Secteur :</span> {getSectorLabel(prospect.sector)}</p>
              <p><span className="text-gray-400">Taille :</span> {prospect.company_size || '-'} employés</p>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Web & Réseaux sociaux
            </h3>
            <div className="space-y-3">
              {prospect.website && (
                <a href={prospect.website} target="_blank" rel="noopener noreferrer" 
                   className="flex items-center gap-2 text-blue-600 hover:underline">
                  <Globe className="w-4 h-4" />
                  Site web
                </a>
              )}
              {prospect.linkedin && (
                <a href={prospect.linkedin} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-blue-700 hover:underline">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </a>
              )}
              {prospect.facebook && (
                <a href={prospect.facebook} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-blue-800 hover:underline">
                  <Facebook className="w-4 h-4" />
                  Facebook
                </a>
              )}
              {prospect.instagram && (
                <a href={prospect.instagram} target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-2 text-pink-600 hover:underline">
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              )}
              {!prospect.website && !prospect.linkedin && !prospect.facebook && !prospect.instagram && (
                <p className="text-gray-400">Aucun lien renseigné</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Notes ({prospect.notes?.length || 0})</h3>
            <Button onClick={() => setShowNoteModal(true)} size="sm" className="bg-blue-600">
              <Plus className="w-4 h-4 mr-1" />
              Ajouter une note
            </Button>
          </div>
          {prospect.notes?.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune note</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {prospect.notes?.map(note => (
                <div key={note.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span>{formatDate(note.created_at)}</span>
                        {note.visible_to_prospect && (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">Visible par le prospect</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'emails' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-4 h-4" />
              Historique des emails ({prospect.emails?.length || 0})
            </h3>
          </div>
          {!prospect.emails?.length ? (
            <div className="p-8 text-center text-gray-400">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun email envoyé</p>
              <p className="text-sm mt-2">Les emails envoyés au prospect apparaîtront ici</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {prospect.emails?.map(email => {
                const emailStatus = emailStatusLabels[email.status] || emailStatusLabels.sent;
                return (
                  <div key={email.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{email.subject}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${emailStatus.color}`}>
                            {emailStatus.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{email.body}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Send className="w-3 h-3" />
                            Envoyé le {formatDate(email.sent_at)}
                          </span>
                          {email.opened_at && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <Check className="w-3 h-3" />
                              Ouvert le {formatDate(email.opened_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'quotes' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Devis ({quotes.length})</h3>
            <Link to={`/admin/crm/quotes?action=new&prospect_id=${prospect.id}`}>
              <Button size="sm" className="bg-blue-600">
                <Plus className="w-4 h-4 mr-1" />
                Nouveau devis
              </Button>
            </Link>
          </div>
          {quotes.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun devis</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {quotes.map(quote => (
                <Link 
                  key={quote.id} 
                  to={`/admin/crm/quotes`}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <p className="font-medium text-gray-900">{quote.quote_number}</p>
                    <p className="text-sm text-gray-500">{formatDate(quote.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{quote.total?.toLocaleString('fr-FR')} €</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusLabels[quote.status]?.color || 'bg-gray-100'}`}>
                      {statusLabels[quote.status]?.label || quote.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Ajouter une note</h2>
              <button onClick={() => setShowNoteModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddNote} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  rows={4}
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Ta note..."
                  required
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newNote.visible_to_prospect}
                  onChange={(e) => setNewNote({ ...newNote, visible_to_prospect: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-600">Visible par le prospect (dans son portail)</span>
              </label>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNoteModal(false)}>Annuler</Button>
                <Button type="submit" className="bg-blue-600">Ajouter</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
